"use client";

import Script from "next/script";
import { useEffect, useSyncExternalStore } from "react";
import { usePathname } from "next/navigation";
import { isGoogleAnalyticsEnabled } from "@/lib/analytics/google-env";
import { AW_ID, GA_ID } from "@/lib/analytics/google-ids";
import { readCookieConsent } from "@/components/analytics/browser-attribution";
import { COOKIE_CONSENT_NAME } from "@/lib/cookie-consent";
import { useAnalyticsSuppressed } from "@/components/analytics/use-analytics-suppressed";

export { AW_ID, GA_ID };

export function googleAdsSendTo(label: string | undefined): string | null {
  if (!label) return null;
  return `${AW_ID}/${label}`;
}

function subscribeToAnalyticsEnvironment() {
  return () => {};
}

function getClientAnalyticsSnapshot() {
  return isGoogleAnalyticsEnabled();
}

function getServerAnalyticsSnapshot() {
  return isGoogleAnalyticsEnabled(process.env.NEXT_PUBLIC_APP_BASE_URL, null);
}

/**
 * Google Analytics loader with Consent Mode v2.
 *
 * Phase 59 refit (2026-04-18):
 * - Consent Mode v2 DEFAULTS live inline in app/layout.tsx <head> — see
 *   .planning/phases/59-render-blocking-content-to-code-ratio-sweep/59-02-PLAN.md.
 *   That inline script defines window.dataLayer, gtag, and calls
 *   gtag('consent','default',{...}) synchronously during HTML parse.
 * - This component only owns the non-blocking parts: the gtag.js fetch (an
 *   effect appends the <script> post-hydration — afterInteractive timing
 *   without the head preload; see the comment on that effect), the ga-init
 *   config calls (strategy=afterInteractive, inline so no preload), and the
 *   post-hydration consent RECONCILIATION (useEffect reads the authoritative
 *   fail-closed cookie choice + listens for cookie_consent_change).
 *
 * The `beforeInteractive` Script that used to live here was the site-wide T1
 * render-blocking driver (163 monitored pages all mounted app/layout.tsx).
 * Removing it clears T1 across the site.
 *
 * Consent ordering contract (Google Consent Mode v2):
 *   1. Inline head <script> registers dataLayer, gtag shim, consent defaults.
 *   2. Page hydrates; this component's useEffect fires.
 *   3. useEffect reads the fail-closed cookie consent choice and calls
 *      gtag('consent','update',...) if the user has a stored choice.
 *   4. gtag.js loads right after hydration with the Ads tag ID, early enough
 *      for Google Ads diagnostics to see the account/conversion tag in the
 *      document; conversions fired before it arrives queue in dataLayer.
 *   5. ga-init config calls fire; gtag.js drains any queued dataLayer events.
 */
export function GoogleAnalytics() {
  const envEnabled = useSyncExternalStore(
    subscribeToAnalyticsEnvironment,
    getClientAnalyticsSnapshot,
    getServerAnalyticsSnapshot,
  );
  // Never load GA on bearer-token paths (or /login|/signup carrying a token in
  // ?redirect_url) — `gtag('config')` would send the token-bearing URL as
  // `page_location`. See lib/analytics/suppressed-paths.ts.
  const pathname = usePathname();
  const suppressed = useAnalyticsSuppressed(pathname);
  const enabled = envEnabled && !suppressed;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const gaDisableKey = `ga-disable-${GA_ID}`;
    const awDisableKey = `ga-disable-${AW_ID}`;
    const win = window as unknown as Record<string, boolean>;
    if (!enabled) {
      // Suppressed route (or analytics disabled). GA may already be loaded from
      // a prior page with granted consent, so a client-side navigation INTO a
      // token route would otherwise let GA4 enhanced measurement auto-send a
      // page_view carrying the token-bearing URL. Hard-disable the GA + Ads
      // tags — the official `ga-disable-<id>` kill switch stops ALL collection,
      // including cookieless pings and queued gtag calls — and deny consent, so
      // nothing leaves a token route once suppression flips on. (Audit P1.)
      win[gaDisableKey] = true;
      win[awDisableKey] = true;
      window.gtag?.("consent", "update", {
        analytics_storage: "denied",
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
      });
      return;
    }
    // Re-enable GA/Ads collection after leaving a suppressed route.
    win[gaDisableKey] = false;
    win[awDisableKey] = false;
    // Reconcile the inline all-denied defaults with the user's stored choice.
    // Only an explicit acceptance grants analytics and advertising storage;
    // a refusal re-asserts denied in case consent changed during this session.
    const stored = readCookieConsent();
    if (stored === "accepted" && window.gtag) {
      window.gtag("consent", "update", {
        analytics_storage: "granted",
        ad_storage: "granted",
        ad_user_data: "granted",
        // Conversion measurement does not require personalised advertising.
        // Keep this separate consent signal denied on both banner outcomes.
        ad_personalization: "denied",
      });
    } else if (stored === "declined" && window.gtag) {
      window.gtag("consent", "update", {
        analytics_storage: "denied",
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
      });
    }

    // Listen for future consent-change events (fires from the cookie banner).
    function onConsent(_event: Event) {
      if (typeof window === "undefined" || !window.gtag) return;
      // Do not trust event detail alone: duplicate path-scoped cookies can
      // conflict, so re-read the same fail-closed state the server will see.
      if (readCookieConsent() === "accepted") {
        window.gtag("consent", "update", {
          analytics_storage: "granted",
          ad_storage: "granted",
          ad_user_data: "granted",
          ad_personalization: "denied",
        });
      } else {
        window.gtag("consent", "update", {
          analytics_storage: "denied",
          ad_storage: "denied",
          ad_user_data: "denied",
          ad_personalization: "denied",
        });
      }
    }
    window.addEventListener("cookie_consent_change", onConsent);
    function onConsentStorage(event: StorageEvent) {
      if (event.key === COOKIE_CONSENT_NAME) onConsent(event);
    }
    window.addEventListener("storage", onConsentStorage);
    return () => {
      window.removeEventListener("cookie_consent_change", onConsent);
      window.removeEventListener("storage", onConsentStorage);
    };
  }, [enabled]);

  // Fetch gtag.js right after hydration by appending the <script> element in
  // an effect, instead of a remote `<Script strategy="afterInteractive">`.
  // Timing is the SAME as afterInteractive (post-hydration — the #409
  // "expose the Ads tag early, don't wait for window.onload" contract holds,
  // so Google Ads tag diagnostics and landing-page gclid capture see the tag
  // as early as before), but a remote afterInteractive Script also makes Next
  // emit a `<link rel="preload">` for it in the initial <head> — ~150 KB of
  // tag JS competing with the render-blocking CSS and the hero webfonts for
  // slow-4G bandwidth in the pre-LCP window (PageSpeed mobile 70, 2026-07,
  // homepage H1 LCP 5.7 s). Effect injection keeps the load timing and drops
  // only the preload hint. The <head> preconnect to googletagmanager.com in
  // app/layout.tsx keeps the connection warm for this fetch. gtag() calls
  // made before the library arrives queue in dataLayer and drain when it
  // loads. lib/seo/__tests__/google-ads-tag-visibility.test.ts pins both
  // sides of this contract.
  useEffect(() => {
    if (!enabled) return;
    if (document.getElementById("gtag-lib")) return;
    const script = document.createElement("script");
    script.id = "gtag-lib";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${AW_ID}`;
    document.head.appendChild(script);
  }, [enabled]);

  if (!enabled) return null;

  return (
    <>
      {/* ga-init stays a next/script (inline scripts emit no head preload) so
          it runs once after hydration alongside the gtag.js fetch above. Use
          the Ads ID as the loader ID so Google Ads tag diagnostics can see the
          conversion account tag; GA4 is configured from the same gtag
          instance. dataLayer is normally already populated by the inline
          <script> in app/layout.tsx <head>, but ga-init re-asserts the
          dataLayer + gtag shim defensively so a stripped/blocked inline snippet
          (privacy extension, CSP, ordering edge case) degrades gracefully instead
          of throwing an unhandled `gtag is not defined` ReferenceError. */}
      <Script id="ga-init" strategy="afterInteractive">
        {`window.dataLayer=window.dataLayer||[];window.gtag=window.gtag||function(){dataLayer.push(arguments);};gtag('js', new Date());gtag('config', '${GA_ID}', { anonymize_ip: true });gtag('config', '${AW_ID}');`}
      </Script>
    </>
  );
}
